import React, { useEffect, useRef } from "react";
import { ActivityIndicator, Animated, Easing, Pressable, StyleSheet, Text, View } from "react-native";
import { PauseIcon, PlayIcon, SquareIcon, SpeakerHighIcon, WarningCircleIcon } from "@phosphor-icons/react";

import { colors, spacing, typographyStyles } from "../../../../styles/design-system";
import { MessageActionButton } from "./MessageActionButton";

export type ReadAloudStatus = "loading" | "playing" | "paused" | "error";

export interface ReadAloudControl {
  status: ReadAloudStatus;
  isActive: boolean;
  hasPlayed: boolean;
  elapsedSeconds: number;
  totalSeconds: number;
  audioLevel?: number;
  audioLevels?: number[];
  showTimer?: boolean;
  onPlayPress: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

export interface ReadAloudPlaybackControlProps {
  readAloudControl: ReadAloudControl;
}

const MIN_WAVE_BAR_HEIGHT = 3;
const WAVE_BAR_WIDTH = 2;
const WAVE_BAR_GAP = 1;
const waveHeights = [8, 14, 11, 18, 10, 15, 9, 17, 8, 14, 11, 18, 10, 15, 9, 8];
const MAX_WAVE_HEIGHT = Math.max(...waveHeights);

const formatTime = (seconds: number) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const ReadAloudIndicator = ({
  audioLevel,
  audioLevels,
  elapsedSeconds,
  onPause,
  onResume,
  showTimer = true,
  status,
  totalSeconds,
}: Pick<
  ReadAloudControl,
  "audioLevel" | "audioLevels" | "elapsedSeconds" | "onPause" | "onResume" | "showTimer" | "status" | "totalSeconds"
>) => {
  const waveAnimationsRef = useRef(waveHeights.map(() => new Animated.Value(0)));
  const waveLoopsRef = useRef<Animated.CompositeAnimation[]>([]);
  const reactiveAnimationFrameRef = useRef<number | null>(null);
  const lastReactiveTickAtRef = useRef<number>(0);
  const externalLevelRef = useRef(0);
  const smoothedLevelRef = useRef(0);
  const waveAnimations = waveAnimationsRef.current;
  const isAudioReactive = typeof audioLevel === "number";
  const hasAudioLevels = Array.isArray(audioLevels) && audioLevels.length > 0;
  const normalizedAudioLevel = Number.isFinite(audioLevel ?? 0) ? Math.max(0, Math.min(1, audioLevel ?? 0)) : 0;

  useEffect(() => {
    externalLevelRef.current = normalizedAudioLevel;
  }, [normalizedAudioLevel]);

  useEffect(() => {
    waveLoopsRef.current.forEach((animation) => animation.stop());
    waveLoopsRef.current = [];
    if (reactiveAnimationFrameRef.current != null) {
      cancelAnimationFrame(reactiveAnimationFrameRef.current);
      reactiveAnimationFrameRef.current = null;
    }

    if (isAudioReactive) {
      const animateReactiveWave = (now: number) => {
        if (lastReactiveTickAtRef.current === 0) {
          lastReactiveTickAtRef.current = now;
        }
        const dt = Math.min(100, now - lastReactiveTickAtRef.current);
        lastReactiveTickAtRef.current = now;

        const inputLevel = status === "playing" ? externalLevelRef.current : 0;
        const riseAlpha = 1 - Math.pow(0.1, dt / 16.67);
        const fallAlpha = 1 - Math.pow(0.02, dt / 16.67);
        const alpha = inputLevel > smoothedLevelRef.current ? riseAlpha : fallAlpha;
        smoothedLevelRef.current = smoothedLevelRef.current + (inputLevel - smoothedLevelRef.current) * alpha;

        if (status !== "playing") {
          const decayAlpha = 1 - Math.pow(0.01, dt / 16.67);
          smoothedLevelRef.current = smoothedLevelRef.current * (1 - decayAlpha);
        }

        waveAnimations.forEach((animatedValue, index) => {
          const normalizedBarWeight = waveHeights[index] / MAX_WAVE_HEIGHT;
          const targetLevel = smoothedLevelRef.current * (0.35 + normalizedBarWeight * 0.65);
          animatedValue.setValue(targetLevel);
        });

        reactiveAnimationFrameRef.current = requestAnimationFrame(animateReactiveWave);
      };

      reactiveAnimationFrameRef.current = requestAnimationFrame(animateReactiveWave);
      return () => {
        if (reactiveAnimationFrameRef.current != null) {
          cancelAnimationFrame(reactiveAnimationFrameRef.current);
          reactiveAnimationFrameRef.current = null;
        }
        lastReactiveTickAtRef.current = 0;
      };
    }

    if (status === "paused") {
      waveAnimations.forEach((animatedValue) => {
        animatedValue.stopAnimation();
      });
      return;
    }

    if (status !== "playing") {
      waveAnimations.forEach((animatedValue) => {
        animatedValue.stopAnimation(() => {
          animatedValue.setValue(0);
        });
      });
      return;
    }

    waveLoopsRef.current = waveAnimations.map((animatedValue, index) => {
      const riseDuration = 280 + (index % 5) * 60;
      const fallDuration = 420 + (index % 4) * 70;
      animatedValue.setValue(index % 2 === 0 ? 0.15 : 0.65);

      const animation = Animated.loop(
        Animated.sequence([
          Animated.timing(animatedValue, {
            toValue: 1,
            duration: riseDuration,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
          Animated.timing(animatedValue, {
            toValue: 0,
            duration: fallDuration,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
        ])
      );

      animation.start();
      return animation;
    });

    return () => {
      waveLoopsRef.current.forEach((animation) => animation.stop());
      waveLoopsRef.current = [];
    };
  }, [isAudioReactive, status, waveAnimations]);

  const isPaused = status === "paused";
  const isLoading = status === "loading";
  const isInactiveIndicator = isPaused || isLoading;

  return (
    <View style={styles.readAloudIndicator}>
      <Pressable
        disabled={isLoading}
        onPress={isPaused ? onResume : onPause}
        style={(state) => {
          const hovered = (state as { hovered?: boolean }).hovered;
          return [
            styles.indicatorPlaybackButton,
            (hovered || state.pressed) && !isLoading && styles.indicatorPlaybackButtonActive,
          ];
        }}
        aria-label={isLoading ? "Loading read aloud" : isPaused ? "Resume reading" : "Pause reading"}
        accessibilityLabel={isLoading ? "Loading read aloud" : isPaused ? "Resume reading" : "Pause reading"}
        accessibilityHint="Press to control read aloud playback">
        {isLoading ? (
          <ActivityIndicator
            size={12}
            color={colors.grey[500]}
            accessibilityLabel="Loading read aloud"
            aria-label="Loading read aloud"
          />
        ) : isPaused ? (
          <PlayIcon size={14} color={colors.grey[500]} weight="fill" />
        ) : (
          <PauseIcon size={14} color={colors.grey[500]} weight="fill" />
        )}
      </Pressable>
      <View style={styles.waveformContainer}>
        {waveHeights.map((height, index) =>
          hasAudioLevels ? (
            <View
              key={index}
              style={[
                styles.waveBar,
                {
                  height:
                    status === "playing"
                      ? Math.max(
                          MIN_WAVE_BAR_HEIGHT,
                          Math.min(
                            height,
                            (audioLevels[index] ?? audioLevels[audioLevels.length - 1] ?? 0) * height * 0.9
                          )
                        )
                      : MIN_WAVE_BAR_HEIGHT,
                },
                isInactiveIndicator && styles.waveBarPaused,
              ]}
            />
          ) : (
            <Animated.View
              key={index}
              style={[
                styles.waveBar,
                {
                  height: waveAnimations[index].interpolate({
                    inputRange: [0, 1],
                    outputRange: [MIN_WAVE_BAR_HEIGHT, height],
                  }),
                },
                isInactiveIndicator && styles.waveBarPaused,
              ]}
            />
          )
        )}
      </View>
      {showTimer ? (
        <Text style={styles.indicatorTime}>
          {formatTime(elapsedSeconds)} / {formatTime(totalSeconds)}
        </Text>
      ) : null}
    </View>
  );
};

export const ReadAloudPlaybackControl = ({ readAloudControl }: ReadAloudPlaybackControlProps) => {
  const { isActive, onPlayPress, onStop, status } = readAloudControl;
  const isLoading = status === "loading";
  const isStopState = isActive && (status === "playing" || status === "paused");
  const showError = status === "error" && !isActive;

  const getButtonIcon = (isInteracting: boolean) => {
    if (isLoading) {
      return (
        <ActivityIndicator
          size={14}
          color={colors.brand[600]}
          accessibilityLabel="Loading read aloud"
          aria-label="Loading read aloud"
        />
      );
    }

    if (showError) {
      return <WarningCircleIcon size={17} color={colors.error[600]} weight="fill" />;
    }

    if (isStopState) {
      return <SquareIcon size={16} color={colors.brand[600]} weight="fill" />;
    }

    return <SpeakerHighIcon size={17} color={isInteracting ? colors.grey[700] : colors.grey[400]} weight="regular" />;
  };

  const actionButtonLabel = isStopState
    ? "Stop reading"
    : isLoading
      ? "Loading read aloud"
      : showError
        ? "Retry read aloud"
        : "Read aloud";
  const tooltipLabel = isLoading ? null : isStopState ? "Stop reading" : showError ? "Retry read aloud" : "Read aloud";
  const readAloudActiveStyle = showError
    ? styles.readAloudButtonError
    : isStopState
      ? styles.readAloudButtonStop
      : undefined;
  const readAloudActiveHoverStyle = showError
    ? styles.readAloudButtonErrorHover
    : isStopState
      ? styles.readAloudButtonStopHover
      : undefined;

  const handleReadAloudActionPress = () => {
    if (isLoading) {
      return;
    }

    if (isStopState) {
      onStop();
      return;
    }

    onPlayPress();
  };

  return (
    <View style={styles.readAloudContainer}>
      <MessageActionButton
        tooltipContent={tooltipLabel}
        accessibilityLabel={actionButtonLabel}
        accessibilityHint="Press to control read aloud playback"
        onPress={handleReadAloudActionPress}
        isDisabled={isLoading}
        isActive={Boolean(readAloudActiveStyle)}
        activeStyle={readAloudActiveStyle}
        activeHoverStyle={readAloudActiveHoverStyle}
        disabledStyle={styles.readAloudButtonDisabled}
        icon={(isInteracting) => getButtonIcon(isInteracting)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  readAloudContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  readAloudButtonStop: {
    backgroundColor: colors.brand[100],
  },
  readAloudButtonStopHover: {
    backgroundColor: colors.brand[200],
  },
  readAloudButtonError: {
    borderColor: colors.error[200],
    borderWidth: 1,
    backgroundColor: colors.error[50],
  },
  readAloudButtonErrorHover: {
    borderColor: colors.error[300],
    borderWidth: 1,
    backgroundColor: colors.error[100],
  },
  readAloudButtonDisabled: {
    opacity: 0.7,
  },
  readAloudIndicator: {
    alignSelf: "flex-start",
    marginBottom: spacing.xxs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xxs,
    borderRadius: 12,
    backgroundColor: "rgba(242, 244, 247, 0.6)",
    borderWidth: 1,
    borderColor: colors.grey[100],
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    minHeight: 44,
  },
  indicatorPlaybackButton: {
    width: 24,
    height: 24,
    paddingTop: spacing.xxs,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  indicatorPlaybackButtonActive: {
    backgroundColor: colors.grey[200],
  },
  waveformContainer: {
    flexShrink: 0,
    height: 18,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: WAVE_BAR_GAP,
  },
  waveBar: {
    width: WAVE_BAR_WIDTH,
    borderRadius: 1,
    backgroundColor: "rgba(154, 164, 255, 0.95)",
  },
  waveBarPaused: {
    backgroundColor: "rgba(154, 164, 255, 0.5)",
  },
  indicatorTime: {
    flexShrink: 0,
    color: colors.grey[400],
    ...typographyStyles.textXs,
    fontVariant: ["tabular-nums"],
    minWidth: 78,
    textAlign: "right",
  },
});
